// React error boundary — catches render errors in children and shows
// a fallback UI with retry instead of crashing to a white screen.

import { Button } from '@aod/ui/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Component, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
  readonly fallbackLabel?: string;
}

interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  override render() {
    if (this.state.error) {
      return (
        <div className="fixed inset-0 z-[9999] grid place-items-center bg-background/95 p-6 text-center backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 font-mono text-sm max-w-md">
            <AlertTriangle className="size-8 text-destructive" aria-hidden />
            <div>
              <p className="text-destructive font-medium text-base">
                {this.props.fallbackLabel ?? 'Something went wrong'}
              </p>
              <p className="text-muted-foreground text-xs mt-2 break-all">
                {this.state.error.message}
              </p>
            </div>
            <Button type="button" variant="secondary" onClick={this.handleRetry}>
              <RefreshCw className="size-4" aria-hidden />
              Retry
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
